import { logDebug, logWarn } from './utils/logger';

const STORAGE_KEY_PREFIX = 'human_behavior_';

export interface QueuedEvent {
    sessionId: string;
    events: any[];
    endUserId?: string | null;
    windowId?: string;
    automaticProperties?: any;
    timestamp: number;
}

export class EventPersistence {
    private storageKey: string;
    private maxQueueSize: number;

    constructor(apiKey: string, maxQueueSize: number = 1000) {
        this.storageKey = `${STORAGE_KEY_PREFIX}queue`;
        this.maxQueueSize = maxQueueSize;
    }

    /**
     * Get persisted events from storage
     */
    getQueue(): QueuedEvent[] {
        if (typeof window === 'undefined' || !window.localStorage) {
            return [];
        }

        try {
            const stored = window.localStorage.getItem(this.storageKey);
            if (!stored) {
                return [];
            }

            const queue = JSON.parse(stored);
            if (!Array.isArray(queue)) {
                return [];
            }

            return queue;
        } catch (error) {
            logWarn('Failed to read persisted queue:', error);
            return [];
        }
    }

    /**
     * Save events to storage
     */
    setQueue(queue: QueuedEvent[]): void {
        if (typeof window === 'undefined' || !window.localStorage) {
            return;
        }

        try {
            // Limit queue size
            const limitedQueue = queue.slice(-this.maxQueueSize);
            window.localStorage.setItem(this.storageKey, JSON.stringify(limitedQueue));
            logDebug(`Persisted ${limitedQueue.length} events to storage`);
        } catch (error: any) {
            // Handle quota exceeded errors gracefully
            if (error.name === 'QuotaExceededError' || error.code === 22) {
                logWarn('Storage quota exceeded, clearing old events');
                try {
                    // Try to save a smaller queue
                    const smallerQueue = queue.slice(-Math.floor(this.maxQueueSize / 2));
                    window.localStorage.setItem(this.storageKey, JSON.stringify(smallerQueue));
                } catch (e) {
                    logWarn('Failed to save smaller queue, clearing storage');
                    this.clearQueue();
                }
            } else {
                logWarn('Failed to persist queue:', error);
            }
        }
    }

    /**
     * Add event to persisted queue
     */
    addToQueue(event: QueuedEvent): void {
        const queue = this.getQueue();
        queue.push(event);

        // Remove oldest events if queue is too large
        if (queue.length > this.maxQueueSize) {
            queue.shift();
            logDebug('Queue is full, the oldest event is dropped.');
        }

        this.setQueue(queue);
    }

    /**
     * Remove events from queue (after successful send)
     */
    removeFromQueue(count: number): void {
        const queue = this.getQueue();
        queue.splice(0, count);
        this.setQueue(queue);
    }

    /**
     * Clear persisted queue
     */
    clearQueue(): void {
        if (typeof window === 'undefined' || !window.localStorage) {
            return;
        }

        try {
            window.localStorage.removeItem(this.storageKey);
        } catch (error) {
            logWarn('Failed to clear persisted queue:', error);
        }
    }

    /**
     * Get queue length
     */
    getQueueLength(): number {
        return this.getQueue().length;
    }
}

